/**
 * Build a column-index × template matrix for pipe-delimited import mapping preview.
 * @see docs/IMPORT_MAPPING_TEMPLATE_PREVIEW.md
 */

import { resolveSourceLabelToColumn } from './igtPatronTemplate.js'

/** Safety cap if a bad mapping stores a huge index. */
export const IMPORT_MAPPING_PREVIEW_MAX_COL = 200

/**
 * Invert template content: colIndex → { fields: string[], label?: string }
 * @param {object|null|undefined} content — template_content / import mapping cfg
 * @returns {Record<number, { fields: string[], label: string|null }>}|null
 *   null when not pipe-delimited or empty
 */
export function invertPipeMappingToColumns(content) {
  if (!content || typeof content !== 'object') return null
  const fmt = String(content.sourceFormat || '').toLowerCase()
  if (fmt !== 'pipe-delimited' && fmt !== 'pipe') return null

  const mappings = content.mappings && typeof content.mappings === 'object' ? content.mappings : {}
  const sourceLabels =
    content.sourceLabels && typeof content.sourceLabels === 'object' ? content.sourceLabels : {}

  /** @type {Record<number, { fields: string[], label: string|null }>} */
  const byCol = {}

  for (const [psgField, source] of Object.entries(mappings)) {
    if (source == null || String(source).trim() === '') continue
    const col = resolveSourceLabelToColumn(source, sourceLabels)
    if (col == null || Number.isNaN(col) || col < 0) continue
    if (col > IMPORT_MAPPING_PREVIEW_MAX_COL) continue
    if (!byCol[col]) {
      const label = sourceLabels[String(col)] != null ? String(sourceLabels[String(col)]) : null
      byCol[col] = { fields: [], label }
    }
    if (!byCol[col].fields.includes(psgField)) {
      byCol[col].fields.push(psgField)
    }
  }

  // Columns that have a sourceLabels entry but no PSG mapping yet
  for (const [idx, label] of Object.entries(sourceLabels)) {
    const col = parseInt(idx, 10)
    if (Number.isNaN(col) || col < 0 || col > IMPORT_MAPPING_PREVIEW_MAX_COL) continue
    if (!byCol[col]) {
      byCol[col] = { fields: [], label: label != null ? String(label) : null }
    } else if (byCol[col].label == null && label != null) {
      byCol[col].label = String(label)
    }
  }

  return byCol
}

/**
 * @param {{ id: string, name: string, content: object|null }[]} columns
 * @returns {{ columns: typeof columns, maxCol: number, rows: number[] }}
 */
export function buildImportMappingPreviewMatrix(columns) {
  const list = Array.isArray(columns) ? columns.filter((c) => c && c.content) : []
  const inverted = list.map((c) => ({
    ...c,
    byCol: invertPipeMappingToColumns(c.content) || {},
  }))

  let maxCol = -1
  for (const col of inverted) {
    for (const k of Object.keys(col.byCol)) {
      const n = Number(k)
      if (!Number.isNaN(n) && n > maxCol) maxCol = n
    }
  }

  const rows = maxCol < 0 ? [] : Array.from({ length: maxCol + 1 }, (_, i) => i)

  return { columns: inverted, maxCol, rows }
}

export function importMappingTypeLabel(mappingType) {
  const t = String(mappingType || '')
  return (
    {
      meter: 'Meter',
      sessions: 'Sessions',
      egm: 'EGM Registry',
      patron: 'Patron (DimPatron)',
      slot_analysis: 'Slot analysis',
    }[t] || t || 'Import'
  )
}
